import React, { useEffect, useState } from 'react';
import Button from '~/app/common/Button';
import Icon from '~/app/common/Icon';
import Modal from '~/app/common/Modal';
import useTranslation from '~/app/hooks/useTranslation';
import Field from '~/app/modules/form/Field';
import Text from '~/app/modules/form/Text';
import { useStoreActions, useStoreState } from '~/store';
import { Material } from '~/types';
import Quantity from '../../Quantity';
import classes from './style.module.scss';
import { MaterialTypes } from '~/app/data/statuses/issue';
import { IssueOrServiceType } from '~/app/data/statuses/chat';

export interface ManualPartFormProps {
  open: boolean
  onClose: () => void
  material?: Material
  type: IssueOrServiceType
}

type FieldState<T> = {
  value: T;
  touched: boolean;
  dirty: boolean;
};

type MaterialFormState = {
  article_name: FieldState<string>;
  article_no: FieldState<string>;
  price_per_unit: FieldState<string | number>;
  quantity: FieldState<number>;
};

type FieldName = keyof MaterialFormState;

const getInitialFormState = (material?: Material): MaterialFormState => ({
  article_name: { value: material?.article_name ?? '', touched: false, dirty: false },
  article_no: { value: material?.article_no ?? '', touched: false, dirty: false },
  price_per_unit: { value: material?.price_per_unit ?? '', touched: false, dirty: false },
  quantity: { value: material?.quantity ?? 0, touched: false, dirty: false },
});

const isFormValid = (state: MaterialFormState) =>
  state.article_name.value.trim() &&
  state.article_no.value.trim() &&
  state.price_per_unit.value !== '' &&
  Number(state.price_per_unit.value) > 0;

export type ManualPartFormomponent = React.FC<ManualPartFormProps>

const ManualPartForm: ManualPartFormomponent = ({ open, onClose, material, type }) => {

  const basePath = (type === IssueOrServiceType.ISSUE )? 'issue' : 'service';
  const tempMaterials = useStoreState(state => state[basePath].tempMaterials ?? []);
  const setTempMaterialsAction = useStoreActions(actions => actions[basePath].setTempMaterials);
  const [formState, setFormState] = useState<MaterialFormState>(getInitialFormState(material));
  const { t } = useTranslation('material');

  useEffect(() => {
    setFormState(getInitialFormState(material));
  }, [material]);

  const updateField = (field: FieldName, value: string | number) => {
    setFormState(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        dirty: value !== (material?.[field] ?? (typeof value === 'number' ? 0 : '')),
      },
    }));
  };

  const markTouched = (field: FieldName) => {
    setFormState(prev => ({
      ...prev,
      [field]: { ...prev[field], touched: true },
    }));
  };

  const handleFormSubmit = () => {
    if (!isFormValid(formState)) {
      setFormState(prev =>
        Object.fromEntries(
          Object.entries(prev).map(([key, field]) => [key, { ...field, touched: true }])
        ) as MaterialFormState
      );
      return;
    }

    const { article_no, article_name, price_per_unit, quantity } = formState;
    const priceNum = Number(price_per_unit.value);

    const updatedMaterials = [...tempMaterials];
    const newMaterialData = {
      article_no: article_no.value,
      article_name: article_name.value,
      price_per_unit: priceNum,
      quantity: quantity.value,
    };

    if (material?.id) {
      const index = updatedMaterials.findIndex(m => m.id === material.id);
      if (index >= 0) {
        updatedMaterials[index] = { ...material, ...newMaterialData };
      } else {
        updatedMaterials.push({ ...material, ...newMaterialData, added: true });
      }
    } else {
      updatedMaterials.push({
        id: `Manual_${article_no.value}${Math.random().toExponential(0)}`,
        ...newMaterialData,
        type: MaterialTypes.MANUAL,
        added: true,
        in_stock: Infinity
      });
    }

    setTempMaterialsAction(updatedMaterials);
    onCloseDialog();
  };

  const fields = [
    { name: 'article_name', label: t('name'), placeholder: t('name'), type: 'text' },
    { name: 'article_no', label: t('articleNo'), placeholder: t('articleNo'), type: 'text' },
    { name: 'price_per_unit', label: t('salePrice'), placeholder: t('salePrice'), type: 'number' }
  ] as const;

  const getError = (name: string, value: any, touched: boolean) => {
    if (!touched) return undefined;
    if (name === 'price_per_unit') {
      return value === '' || Number(value) <= 0 ? ['required'] : undefined;
    }
    return !value.toString().trim() ? ['required'] : undefined;
  };

  const onCloseDialog = () => {
    setFormState(getInitialFormState(material));
    onClose();
  }

  return (
    <Modal className={classes['manualPart']} open={open} onClose={onCloseDialog} position={'bottom'}>
      <div className={classes['partManager']}>
        <div className={classes['partsHeader']}>
          <Icon name='arrow_back' onClick={onCloseDialog} />
          <span>{t('add')} {t('parts')}</span>
        </div>
        <div className={classes['partForm']}>
          {fields.map(({ name, label, placeholder, type }) => (
            <Text
              key={name}
              name={name}
              label={label}
              placeholder={placeholder}
              type={type}
              value={formState[name].value.toString()}
              required
              errors={getError(name, formState[name].value, formState[name].touched)}
              onChange={e => updateField(name, e.target.value)}
              onBlur={() => markTouched(name)}
            />
          ))}
          <Field label={t('quantity')}>
            <Quantity quantity={formState.quantity.value} quantityChange={q => updateField('quantity', q)} />
          </Field>
          <Button className={classes['addButton']} onClick={handleFormSubmit}>{t('save')}</Button>
        </div>
      </div>
    </Modal>
  )
}

export default ManualPartForm;